import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, LogOut, Users, Edit, Check, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UsersManagementPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [userFormData, setUserFormData] = useState({
    nome: '',
    cognome: '',
    ruolo: '',
    ambiti: []  // Array di ambiti
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users`, getAuthHeaders());
      setUsers(response.data);
    } catch (error) {
      console.error('Errore nel caricamento utenti:', error);
      if (error.response?.status === 401) {
        toast.error('Sessione scaduta. Effettua nuovamente il login.');
        onLogout();
      } else if (error.response?.status === 403) {
        toast.error('Non hai i permessi per accedere a questa sezione.');
        navigate('/');
      } else {
        toast.error('Errore nel caricamento degli utenti');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (userToEdit) => {
    setEditingUser(userToEdit);
    // Supporta sia il vecchio campo ambito che il nuovo ambiti
    const ambiti = userToEdit.ambiti && userToEdit.ambiti.length > 0 
      ? userToEdit.ambiti 
      : (userToEdit.ambito ? [userToEdit.ambito] : []);
    setUserFormData({
      nome: userToEdit.nome,
      cognome: userToEdit.cognome,
      ruolo: userToEdit.ruolo,
      ambiti: ambiti
    });
  };

  const handleUpdateUser = async (userId) => {
    try {
      // Prepara i dati con ambiti multipli
      const updateData = {
        nome: userFormData.nome,
        cognome: userFormData.cognome,
        ruolo: userFormData.ruolo,
        ambiti: userFormData.ambiti,
        // Retrocompatibilità: ambito = primo elemento dell'array
        ambito: userFormData.ambiti.length > 0 ? userFormData.ambiti[0] : null
      };
      
      await axios.put(`${API}/users/${userId}`, updateData, getAuthHeaders());
      toast.success('Utente aggiornato con successo');
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Errore aggiornamento utente:', error);
      const errorMsg = error.response?.data?.detail || 'Errore nell\'aggiornamento dell\'utente';
      toast.error(errorMsg);
    }
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
    setUserFormData({
      nome: '',
      cognome: '',
      ruolo: '',
      ambiti: []
    });
  };

  const toggleAmbito = (ambito) => {
    setUserFormData(prev => ({
      ...prev,
      ambiti: prev.ambiti.includes(ambito)
        ? prev.ambiti.filter(a => a !== ambito)
        : [...prev.ambiti, ambito]
    }));
  };

  const handleDeleteUser = async (userId, userEmail) => {
    // Conferma cancellazione
    if (!window.confirm(`Sei sicuro di voler cancellare l'utente ${userEmail}?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/users/${userId}`, getAuthHeaders());
      toast.success('Utente cancellato con successo');
      fetchUsers();
    } catch (error) {
      console.error('Errore cancellazione utente:', error);
      const errorMsg = error.response?.data?.detail || 'Errore nella cancellazione dell\'utente';
      toast.error(errorMsg);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-red-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 py-3 sm:px-6 sm:py-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/')}
                className="border-slate-300"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <div className="flex flex-wrap items-center gap-3">
                <div className="bg-red-600 p-2 rounded-lg">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Gestione Utenti</h1>
                  <p className="text-sm text-slate-600">Amministra utenti del sistema</p>
                </div>
              </div>
            </div>
            
            <Button
              variant="outline"
              onClick={onLogout}
              className="border-slate-300"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Esci
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-8">
        <div className="bg-white rounded-xl shadow-md border border-slate-200">
          {loading ? (
            <div className="p-8 text-center text-slate-600">
              Caricamento utenti...
            </div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600">Nessun utente registrato</p>
            </div>
          ) : (
            <div className="p-6">
              <div className="mb-4">
                <h2 className="text-xl font-semibold text-slate-900">Utenti Registrati</h2>
                <p className="text-sm text-slate-600 mt-1">{users.length} utente{users.length !== 1 ? 'i' : ''} nel sistema</p>
              </div>

              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Nome</TableHead>
                    <TableHead className="font-semibold">Email</TableHead>
                    <TableHead className="font-semibold">Ruolo</TableHead>
                    <TableHead className="font-semibold">Ambito</TableHead>
                    <TableHead className="text-right font-semibold">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell>
                        {editingUser?.id === u.id ? (
                          <div className="flex gap-2">
                            <Input
                              value={userFormData.nome}
                              onChange={(e) => setUserFormData({...userFormData, nome: e.target.value})}
                              className="w-28"
                              placeholder="Nome"
                            />
                            <Input
                              value={userFormData.cognome}
                              onChange={(e) => setUserFormData({...userFormData, cognome: e.target.value})}
                              className="w-28"
                              placeholder="Cognome"
                            />
                          </div>
                        ) : (
                          <span className="font-medium">{u.nome} {u.cognome}</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-sm text-slate-600">{u.email}</TableCell>
                      <TableCell>
                        {editingUser?.id === u.id ? (
                          <Select 
                            value={userFormData.ruolo} 
                            onValueChange={(value) => setUserFormData({...userFormData, ruolo: value})}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="guest">Guest</SelectItem>
                              <SelectItem value="master">Master</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            u.ruolo === 'admin' ? 'bg-red-100 text-red-700' :
                            u.ruolo === 'master' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {u.ruolo === 'admin' ? '🔴 Admin' : u.ruolo === 'master' ? '🔵 Master' : '⚪ Guest'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingUser?.id === u.id ? (
                          <div className="flex flex-wrap gap-2">
                            {[
                              { id: 'amministrativo', label: 'Amministrativo', color: 'blue' },
                              { id: 'ingegnere', label: 'Ingegnere', color: 'green' },
                              { id: 'cantierista', label: 'Cantierista', color: 'orange' },
                              { id: 'fattibilita', label: 'Fattibilità', color: 'purple' }
                            ].map(amb => (
                              <label 
                                key={amb.id} 
                                className={`flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer border transition-all ${
                                  userFormData.ambiti.includes(amb.id)
                                    ? amb.color === 'blue' ? 'bg-blue-100 border-blue-400 text-blue-700' :
                                      amb.color === 'green' ? 'bg-green-100 border-green-400 text-green-700' :
                                      amb.color === 'orange' ? 'bg-orange-100 border-orange-400 text-orange-700' :
                                      'bg-purple-100 border-purple-400 text-purple-700'
                                    : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={userFormData.ambiti.includes(amb.id)}
                                  onChange={() => toggleAmbito(amb.id)}
                                  className="rounded border-slate-300"
                                />
                                <span className="text-xs font-medium">{amb.label}</span>
                              </label>
                            ))}
                          </div>
                        ) : (
                          (() => {
                            // Mostra ambiti multipli o singolo
                            const ambiti = u.ambiti && u.ambiti.length > 0 ? u.ambiti : (u.ambito ? [u.ambito] : []);
                            return ambiti.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {ambiti.map(amb => (
                                  <span key={amb} className={`text-sm px-2 py-1 rounded ${
                                    amb === 'cantierista' ? 'bg-orange-100 text-orange-700' :
                                    amb === 'ingegnere' ? 'bg-green-100 text-green-700' :
                                    amb === 'amministrativo' ? 'bg-blue-100 text-blue-700' :
                                    amb === 'fattibilita' ? 'bg-purple-100 text-purple-700' :
                                    'text-slate-600'
                                  }`}>
                                    {amb === 'cantierista' ? '🟠 Cantierista' : 
                                     amb === 'ingegnere' ? '🟢 Ingegnere' :
                                     amb === 'amministrativo' ? '🔵 Amministrativo' :
                                     amb === 'fattibilita' ? '🟣 Fattibilità' :
                                     amb}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-slate-400 text-sm">Nessuno</span>
                            );
                          })()
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {editingUser?.id === u.id ? (
                          <div className="flex flex-wrap justify-end gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleUpdateUser(u.id)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Salva
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleCancelEdit}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Annulla
                            </Button>
                          </div>
                        ) : (
                          <div className="flex flex-wrap justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEditUser(u)}
                              className="hover:bg-blue-50"
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Modifica
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteUser(u.id, u.email)}
                              className="hover:bg-red-50 text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Cancella
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Legenda Ruoli */}
              <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h3 className="text-sm font-semibold text-slate-900 mb-3">📋 Legenda Ruoli</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">🔴 Admin</span>
                    <p className="text-xs text-slate-600 mt-1">Accesso completo + Gestione Utenti</p>
                  </div>
                  <div>
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">🔵 Master</span>
                    <p className="text-xs text-slate-600 mt-1">Tutte le sezioni tranne Gestione Utenti</p>
                  </div>
                  <div>
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">⚪ Guest</span>
                    <p className="text-xs text-slate-600 mt-1">Solo "I Miei Progetti" (default)</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UsersManagementPage;
